/**
 * Vincent B1033 (Eric 2026-02-26 SPEC STRICK PROD) — Encadré de révision
 * factuelle affiché sous chaque passage cité de la synthèse.
 *
 * Vincent B1034 (Eric 2026-02-26 SPEC STRICK PROD) — Encadré désormais
 * collapsible via un chevron (▸/▾) en haut à droite. Titre toujours
 * visible : « Révision / précision ». Ouvert par défaut (le user voit
 * immédiatement les révisions au premier chargement) mais repliable
 * pour désencombrer la lecture d'une synthèse déjà bien vérifiée.
 *
 * Sémantique (choix Eric, session 2026-02-26) :
 *   - Q1 : les révisions viennent du bouton « Vérifier sur le web ».
 *   - Q2 : la version originale reste éternellement affichée sous
 *          l'encadré (traçabilité pure, pas d'écrasement).
 *   - Q3 : l'URL vient directement du résultat de la vérification web.
 *   - Q4 : seuls Concepteur / Admin / Fondateur peuvent lancer la vérif
 *          (contrôlé côté agent.py via `_require_kb_editor`).
 *   - Q5 : seuls Admin / Fondateur peuvent masquer une révision (Phase 2).
 *   - Q6 : portée limitée à la Synthèse.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LockPassageButton } from "./SynthesisLocks";
import { ArrowRight, CaretDown, CaretRight, Cpu, EyeSlash, Globe, PencilSimple, ShieldCheck } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;


const _fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-CA", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso.slice(0, 16);
  }
};


export default function SynthesisReviewCard({
  claim,
  index,
  open: controlledOpen,
  onToggle: controlledOnToggle,
  canHide,
  videoId,
  onHidden,
  onLocked,
  legacyReplaced = false,  // B1113 — acceptation d'avant la conservation de l'original
}) {
  const [localOpen, setLocalOpen] = useState(true);
  const [hiding, setHiding] = useState(false);
  if (!claim || !claim.suggested_correction) return null;
  // Vincent B1035 (Eric 2026-02-26 SPEC STRICK PROD) — Composant
  // contrôlé si `open` + `onToggle` sont passés (batch expand/collapse
  // depuis SynthesisPanel), non-contrôlé sinon (fallback interne).
  const isControlled = typeof controlledOpen === "boolean" && typeof controlledOnToggle === "function";
  const open = isControlled ? controlledOpen : localOpen;
  const toggle = isControlled ? controlledOnToggle : () => setLocalOpen((v) => !v);
  const {
    id,
    suggested_correction: correction,
    issue,
    source,
    confidence,
    verified_at: at,
    verified_by: by,
    provider,
  } = claim;
  const confidencePct = typeof confidence === "number" ? Math.round(confidence * 100) : null;
  const sourceIsUrl = typeof source === "string" && /^https?:\/\//i.test(source);
  const testKey = id || index || 0;
  // Vincent B1037 (Eric 2026-02-26 SPEC STRICK PROD) — Badge Manuel/IA
  // pour tracer l'origine de la révision. `provider="manual"` → Manuel,
  // sinon (gpt-5.2, perplexity-sonar-pro, etc.) → IA + provider en tooltip.
  const isManual = provider === "manual";
  // Vincent B1038 (Eric 2026-02-26 SPEC STRICK PROD) — Handler « Masquer »
  // (Admin/Fondateur only). POST /synthesis/revisions/{id}/hide → refresh.
  const handleHide = async (e) => {
    e?.stopPropagation();
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      "Masquer définitivement cette révision ? Elle sera conservée en base pour l'audit mais n'apparaîtra plus dans la synthèse.",
    )) return;
    setHiding(true);
    try {
      await axios.post(
        `${API}/api/videos/${videoId}/synthesis/revisions/${id}/hide`,
        {},
        { withCredentials: true },
      );
      toast.success("Révision masquée.");
      if (onHidden) await onHidden();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors du masquage");
    } finally {
      setHiding(false);
    }
  };

  return (
    <aside
      role="note"
      className="my-3 rounded-lg border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3 not-prose"
      data-testid={`synthesis-review-card-${testKey}`}
      data-open={open ? "true" : "false"}
    >
      {/* Header collapsible — B1034. Toujours visible (ouvert ou fermé).
          Le titre "Révision / précision" est le libellé stable qui reste
          affiché quand l'encadré est fermé. Le chevron pivote pour
          indiquer l'état. Bouton entier cliquable pour maximiser la
          zone de toggle (accessibilité + mobile). */}
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-1.5 text-[11px] text-amber-900 dark:text-amber-200 -m-1 p-1 rounded hover:bg-amber-100/60 dark:hover:bg-amber-900/40"
        aria-expanded={open}
        aria-controls={`synthesis-review-body-${testKey}`}
        data-testid={`synthesis-review-card-toggle-${testKey}`}
        title={open ? "Réduire cette révision" : "Développer cette révision"}
      >
        <ShieldCheck size={13} weight="fill" className="text-amber-700 dark:text-amber-300 shrink-0" />
        <span className="font-semibold">Révision / précision</span>
        {/* Vincent B1037 (Eric 2026-02-26 SPEC STRICK PROD) — Badge
            d'origine (Manuel = crayon zinc, IA = puce ambre + tooltip
            provider). Toujours visible (ouvert ET fermé). */}
        {isManual ? (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-zinc-200 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-100 text-[10px] font-semibold"
            data-testid={`synthesis-review-card-badge-manual-${testKey}`}
            title="Révision ajoutée manuellement par un rédacteur"
          >
            <PencilSimple size={9} weight="bold" />
            Manuel
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-800/60 text-amber-900 dark:text-amber-100 text-[10px] font-semibold"
            data-testid={`synthesis-review-card-badge-ai-${testKey}`}
            title={`Révision générée automatiquement${provider ? ` (${provider})` : ""}`}
          >
            <Cpu size={9} weight="bold" />
            IA
          </span>
        )}
        {/* B1113 (Eric 2026-09-08) — Statut de la révision. Le corps de la
            synthèse n'est plus réécrit à l'acceptation : sans ce badge, rien
            ne distinguerait une révision acceptée d'une révision en attente,
            les deux s'affichant sous la même phrase. (Une révision refusée
            est masquée : elle n'a pas d'encadré du tout.) */}
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
            claim?.applied
              ? "bg-teal-100 dark:bg-teal-900/50 text-teal-900 dark:text-teal-100"
              : "bg-white/70 dark:bg-zinc-800 text-amber-900 dark:text-amber-200 border border-amber-300"
          }`}
          data-testid={`synthesis-review-card-status-${testKey}`}
          title={claim?.applied
            ? (legacyReplaced
                ? "Révision acceptée avant la conservation du texte original : le corps affiché contient déjà le texte corrigé."
                : "Révision acceptée — le texte original est conservé au-dessus.")
            : "Révision proposée, pas encore acceptée."}
        >
          {claim?.applied ? "Acceptée" : "En attente"}
        </span>
        {claim?.applied && legacyReplaced && (
          <span
            className="text-[10px] text-amber-800 dark:text-amber-300 italic"
            data-testid={`synthesis-review-card-legacy-${testKey}`}
            title="Cette acceptation date d'avant B1113 : le corps a été réécrit à l'époque, le texte d'origine n'apparaît que dans cet encadré."
          >
            texte déjà remplacé
          </span>
        )}
        {confidencePct !== null && (
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-800/60 text-amber-900 dark:text-amber-100"
            title={`Confiance : ${confidencePct}%`}
          >
            {confidencePct}%
          </span>
        )}
        <span className="ml-auto shrink-0 text-amber-700 dark:text-amber-300" aria-hidden="true">
          {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
        </span>
      </button>

      {open && (
        <div id={`synthesis-review-body-${testKey}`} className="mt-2">
          <p className="text-[10px] text-amber-900/80 dark:text-amber-200/80 mb-1.5">
            Vérifiée le <span className="font-mono">{_fmtDate(at)}</span>
            {" "}par <span className="font-mono">{by || "?"}</span>
          </p>

          <div className="flex items-start gap-2 text-sm text-zinc-900 dark:text-zinc-100">
            <ArrowRight size={14} weight="bold" className="mt-0.5 text-amber-700 dark:text-amber-300 shrink-0" />
            <p className="font-semibold leading-snug" data-testid="synthesis-review-card-correction">
              {correction}
            </p>
          </div>

          {issue && (
            <p className="text-[12px] italic text-zinc-700 dark:text-zinc-300 mt-1.5 pl-6" data-testid="synthesis-review-card-issue">
              {issue}
            </p>
          )}

          {source && (
            <footer className="mt-2 pl-6 flex items-center gap-1.5 text-[11px]">
              <Globe size={12} weight="bold" className="text-amber-700 dark:text-amber-300" />
              <span className="text-amber-900 dark:text-amber-200 opacity-80">Source :</span>
              {sourceIsUrl ? (
                <a
                  href={source}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 dark:text-blue-300 hover:underline truncate max-w-md"
                  data-testid="synthesis-review-card-source"
                >
                  {source}
                </a>
              ) : (
                <span className="text-zinc-700 dark:text-zinc-300 truncate max-w-md" data-testid="synthesis-review-card-source">
                  {source}
                </span>
              )}
              {provider && (
                <span className="ml-2 text-[10px] text-zinc-500 dark:text-zinc-400" title={`Fournisseur : ${provider}`}>
                  via {provider}
                </span>
              )}
            </footer>
          )}

          {/* Vincent B1038 (Eric 2026-02-26 SPEC STRICK PROD) — Bouton
              « Masquer » réservé aux Admin/Fondateur. Marque le claim
              hidden=true côté backend (audit trail conservé), la carte
              disparaît de la synthèse au prochain refresh. */}
          {/* B1072 (Eric 2026-09-04 SPEC STRICK PROD) — Verrouiller la
              correction : la prochaine régénération réécrit tout le texte
              depuis le transcript et effacerait ce passage. */}
          {videoId && (
            <div className="mt-2 pl-6 flex justify-start">
              <LockPassageButton
                videoId={videoId}
                lockedText={correction}
                originalText={claim.original_text || ""}
                note={issue || ""}
                onLocked={onLocked}
                testId={`synthesis-review-card-lock-btn-${testKey}`}
                compact
              />
            </div>
          )}
          {canHide && videoId && id && (
            <div className="mt-2 pl-6 flex justify-end">
              <button
                type="button"
                onClick={handleHide}
                disabled={hiding}
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-600 dark:text-zinc-400 hover:text-red-700 dark:hover:text-red-300 disabled:opacity-50"
                data-testid={`synthesis-review-card-hide-btn-${testKey}`}
                title="Masquer définitivement cette révision (Admin/Fondateur)"
              >
                <EyeSlash size={11} weight="bold" />
                {hiding ? "Masquage…" : "Masquer cette révision"}
              </button>
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
